import { call, delay, getContext, put, select } from 'redux-saga/effects';
import { SupabaseClient } from '@supabase/supabase-js';
import ddt from '@wix/da-ddt';
import { XhrResponse } from '@wix/da-http-client';
import { PapiDamzSearchAutocomplete } from '@wix/da-papi-types';
import { Database } from '@wix/da-papi-types/lib/supabase';
import { fetchSuccess } from '@wix/da-shared-react/pkg/Stream/withCursor/actions';
import { getStream } from '@wix/da-shared-react/pkg/Stream/base/selectors';
import { drawerStreamId, messageSearchStreamId } from '../../constants';
import { RoomItem, RoomType } from '../../types';
import { fetchAutocompleteSearchSuccess } from '../actions/search';

const logger = ddt.createLogger('damz');

// vespa is impossible to test/work-with locally
export function* fakeFetchAutocompleteSearch() {
  if (process.env.NODE_ENV !== 'production') {
    const roomStream = yield select(getStream, `${drawerStreamId}all`);
    // for storybooks, etc. just return an empty set
    if (!roomStream?.items?.length) {
      yield put(fetchAutocompleteSearchSuccess([]));
    }

    const results: any[] = [];
    const toUser = (user, roomId) => ({ user: { user, roomId } });

    const dmRoom = roomStream.items.find(
      (roomItem: RoomItem) => roomItem.room.room_type === RoomType.DM
    );
    // takes you to a current dm room
    dmRoom && results.push(toUser(dmRoom.dmUser, dmRoom.room.room_id));

    const multiRoom = roomStream.items.find(
      (roomItem: RoomItem) => roomItem.room.room_type === RoomType.MULTI
    );
    if (multiRoom) {
      // takes you to a current multi room
      results.push(toUser(multiRoom.multiUsers[0], multiRoom.room.room_id));
      // fails to find the room
      results.push(toUser(multiRoom.multiUsers[1], '404'));
    }

    delay(1200);
    return { results } as XhrResponse<PapiDamzSearchAutocomplete>;
  }
}

export function* fakeFetchMessageSearchNext(
  processor: (results: any[]) => any
) {
  if (process.env.NODE_ENV !== 'production') {
    const client: SupabaseClient<Database, 'damz'> =
      yield getContext('supaClient');
    const { data, error } = yield client
      .from('message')
      .select('message_id, msg, sent_at')
      // show most recent first so we can manually test on the fly better
      .order('sent_at', { ascending: false })
      .limit(20);
    if (error) {
      logger.error(error);
    }

    // for storybooks, etc. just return an empty set
    let combinedResults = [];
    if (data) {
      const shaped = data.map(d => {
        // add some of the same formatting to those snippets
        const snippet =
          Math.random() >= 0.5
            ? d.msg.replace(/(\S+)/, '<hi>$1</hi><sep />')
            : d.msg.replace(
                /(\S+)/,
                '<sep /> (dbl) <hi>$1</hi> blah blah <hi>$1</hi> <sep />'
              );
        return {
          messageId: d.message_id,
          sentAt: d.sent_at,
          snippet,
        };
      });
      combinedResults = yield call(processor, shaped);
    }

    // try two passes so we can fake inf. scrolling too
    const stream = yield select(getStream, messageSearchStreamId);
    const firstPass =
      Array.isArray(stream?.items) && stream?.items?.length <= 0;

    yield put(
      fetchSuccess({
        streamId: messageSearchStreamId,
        hasMore: firstPass,
        items: combinedResults,
      })
    );
    return;
  }
}
